import React, { ChangeEvent, FormEvent } from "react";
import { LayoutMenuPosition } from "../../types";
import { EditContentFormState } from "./useEditContentFormState";
import { SmartListFields } from "./SmartListFields";

const BUTTON_DISABLED_OPACITY = 0.6;

type EditContentFormProps = {
    state: EditContentFormState;
    onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
    isSaving: boolean;
    showSuccessIndicator: boolean;
    isFallbackEditing: boolean;
};

const renderCustomFields = (
    state: EditContentFormState,
    isSaving: boolean,
    showRequiredLabelWarning: boolean,
): React.ReactNode => {
    const {
        normalizedContentType,
        alias,
        setAlias,
        pageSlug,
        setPageSlug,
        layoutMenuPosition,
        setLayoutMenuPosition,
        position,
        setPosition,
        smartList,
    } = state;

    switch (normalizedContentType) {
        case "list":
            return (
                <div className="form-group">
                    <label>Alias</label>
                    <input
                        type="text"
                        value={alias}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setAlias(event.target.value)
                        }
                        placeholder="e.g., my-awesome-list"
                        disabled={isSaving}
                    />
                </div>
            );
        case "list_smart":
            return (
                <SmartListFields
                    state={smartList}
                    isSaving={isSaving}
                    showRequiredLabelWarning={showRequiredLabelWarning}
                />
            );
        case "page":
            return (
                <div className="form-group">
                    <label>Page Slug</label>
                    <input
                        type="text"
                        value={pageSlug}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setPageSlug(event.target.value)
                        }
                        placeholder="e.g., /my-awesome-page"
                        disabled={isSaving}
                    />
                </div>
            );
        case "menu":
            return (
                <div className="form-group">
                    <label>Layout Menu Position</label>
                    <select
                        value={layoutMenuPosition}
                        onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                            setLayoutMenuPosition(event.target.value as LayoutMenuPosition)
                        }
                        disabled={isSaving}
                    >
                        <option value="Header">Header</option>
                        <option value="Footer">Footer</option>
                    </select>
                </div>
            );
        case "menu_item":
            return (
                <div className="form-group">
                    <label>Position (URL/Slug)</label>
                    <input
                        type="text"
                        value={position}
                        onChange={(event: ChangeEvent<HTMLInputElement>) =>
                            setPosition(event.target.value)
                        }
                        placeholder="e.g., /home or https://example.com"
                        disabled={isSaving}
                    />
                </div>
            );
        default:
            return null;
    }
};

export const EditContentForm: React.FC<EditContentFormProps> = ({
    state,
    onSubmit,
    isSaving,
    showSuccessIndicator,
    isFallbackEditing,
}) => {
    const requiresSmartListLabels =
        state.normalizedContentType === "list_smart" && state.smartList.requiresLabels;
    const isMissingRequiredSmartListLabels =
        requiresSmartListLabels && state.smartList.labels.length === 0;

    const isSubmitDisabled = isSaving || isMissingRequiredSmartListLabels;

    return (
        <form onSubmit={onSubmit} className="edit-content-form">
            {isFallbackEditing && (
                <div className="fallback-banner">
                    Editing with cached data. Please double-check after saving and reopen if
                    possible.
                </div>
            )}
            <div className="form-group">
                <label>
                    Title <span className="field-indicator required">*</span>
                </label>
                <input
                    type="text"
                    value={state.title}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                        state.setTitle(event.target.value)
                    }
                    required
                    placeholder="Enter title..."
                    disabled={isSaving}
                />
            </div>
            {renderCustomFields(state, isSaving, isMissingRequiredSmartListLabels)}
            <div className="modal-actions edit-modal-actions">
                <button
                    type="submit"
                    className="save-button"
                    disabled={isSubmitDisabled}
                    style={{
                        opacity: isSubmitDisabled ? BUTTON_DISABLED_OPACITY : 1,
                        cursor: isSubmitDisabled ? "not-allowed" : "pointer",
                    }}
                >
                    {isSaving ? "Saving..." : "Save Changes"}
                </button>
                {showSuccessIndicator && <div className="save-indicator-modal">✓ Saved</div>}
            </div>
        </form>
    );
};
